import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { WindowLateralAlignment } from "building-types/openings/openingTypesEnum";
import type { ExteriorWindowCasingSettings } from "building-types/settings/exteriorWindowCasingSettings";
import { ArchTopBoolean, WindowShapeTypes } from "building-types/settings/windowTypesEnum";
import type {
  GeneratedWindowPlacement,
  WindowInfill,
  WindowShutters,
  GeneratedWindowPlacementDeprecated,
  GeneratedWindowPlacementV2,
  WindowDescriptor,
} from "building-types/windows/windowTypes";
import { mergeWithDefaultsAndOverrides } from "common/utils/mergeWithDefaultsAndOverrides";
import uid from "common/utils/uid";
import GeomTagGroup from "geom/geomTagGroup";
import { archPropertiesFromWidthRadius } from "./windowHeightTools";
import { windowApronCasingClipsFinish, windowHasCasing, windowSideCasingClipsFinish } from "./windowQuery";

export const WINDOW_FRAME_WIDTH = 1;

export function generatedWindowPlacementDeprecatedNew(
  opts: Partial<GeneratedWindowPlacementDeprecated> &
    Pick<
      GeneratedWindowPlacementDeprecated,
      "descriptor" | "frontRoom" | "halfEdgeIndex" | "param" | "paramAnchor" | "streetOrientation" | "exteriorCasing"
    >,
): GeneratedWindowPlacementDeprecated {
  const id = opts.id ?? uid();

  return mergeWithDefaultsAndOverrides<GeneratedWindowPlacementDeprecated>(
    {
      wallStripDomain: false,
      reverse: false,
      paramAnchor: "START",
      paramNormalized: false,
      headHeight: undefined,
      opening: undefined,
      verticalOffset: 0,
      lateralDatum: WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE,
      lateralAlignment: WindowLateralAlignment.OUTSIDE_FACE,
      lateralOffset: 0,
      lateralOffsetNormalized: false,
      awning: undefined,
      awningSettings: undefined,
    },
    opts,
    {
      materials: { ...opts.descriptor.materials, ...(opts.materials ?? {}) },
      hasExteriorCasing: windowHasCasing(opts, opts.exteriorCasing as ExteriorWindowCasingSettings),
      apronCasingClipsFinish: windowApronCasingClipsFinish(opts, opts.exteriorCasing as ExteriorWindowCasingSettings),
      apronHeight: opts.descriptor.exteriorCasing?.apronHeight || opts.exteriorCasing?.apronHeight || 0,
      sideCasingClipsFinish: windowSideCasingClipsFinish(opts, opts.exteriorCasing as ExteriorWindowCasingSettings),
      type: "WINDOW",
      id,
    },
  );
}

export function generatedWindowPlacementNew(
  opts: Partial<GeneratedWindowPlacementV2> &
    Pick<
      GeneratedWindowPlacementV2,
      "descriptor" | "frontRoom" | "halfEdgeIndex" | "param" | "paramAnchor" | "streetOrientation" | "exteriorCasing"
    >,
): GeneratedWindowPlacementV2 {
  const id = opts.id ?? uid();

  return mergeWithDefaultsAndOverrides<GeneratedWindowPlacementV2>(
    {
      reverse: false,
      paramAnchor: "START",
      paramNormalized: false,
      headHeight: undefined,
      opening: undefined,
      verticalOffset: 0,
      lateralDatum: WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE,
      lateralAlignment: WindowLateralAlignment.OUTSIDE_FACE,
      lateralOffset: 0,
      lateralOffsetNormalized: false,
      awning: undefined,
      awningSettings: undefined,
      materials: { ...opts.descriptor.materials, ...(opts.materials ?? {}) },
    },
    opts,
    {
      hasExteriorCasing: windowHasCasing(opts, opts.exteriorCasing as ExteriorWindowCasingSettings),
      apronCasingClipsFinish: windowApronCasingClipsFinish(opts, opts.exteriorCasing as ExteriorWindowCasingSettings),
      apronHeight: opts.descriptor.exteriorCasing?.apronHeight || opts.exteriorCasing?.apronHeight || 0,
      sideCasingClipsFinish: windowSideCasingClipsFinish(opts, opts.exteriorCasing as ExteriorWindowCasingSettings),
      type: "WINDOW_V2",
      id,
    },
  );
}

export function windowInfillNew(): WindowInfill {
  return {
    interiorWallFill: new GeomTagGroup(),
    exteriorWallFill: new GeomTagGroup(),
  };
}

export function windowShuttersNew(window: GeneratedWindowPlacement, fallbackInstanceId?: string): WindowShutters {
  // Shutters take the same pick handle as the window body: the hosting component instance when the
  // window sits in one, its own opening otherwise. `fallbackInstanceId` covers roof and dormer
  // windows, which have no rooms to read the instance from.
  const instanceId = window.frontRoom?.instanceId ?? window.backRoom?.instanceId ?? fallbackInstanceId;
  const pickHandle: DocEntityHandle | undefined = instanceId
    ? { type: "COMPONENT_INSTANCE", id: instanceId }
    : window.handle;

  return {
    window,
    decorations: new GeomTagGroup(),
    pickHandle,
  };
}

/**
 * The operable height of a window is the space that an operable panel can occupy.
 */

export function getWindowOperableHeight(descriptor: WindowDescriptor) {
  const { shape, archRadius, archBoolean, height, width, arched } = descriptor;

  const operableHeight = height - WINDOW_FRAME_WIDTH;
  const isWindowArched =
    (shape === WindowShapeTypes.RECTANGLE_ARCHED_TOP && archBoolean === ArchTopBoolean.SUBTRACT) ||
    (shape === WindowShapeTypes.RECTANGLE && arched);

  if (isWindowArched) {
    const { height: archHeight, arcAngle } = archPropertiesFromWidthRadius(width, archRadius);
    // the scale factor is the ratio of the arc's tangent angle at its spring line (which is also the
    // arc angle) to 90 degrees. this is used to account for the frame offset at the spring line.
    const scaleFactor = 1 - arcAngle / (Math.PI / 2);
    return operableHeight - archHeight - scaleFactor * WINDOW_FRAME_WIDTH;
  } else {
    return operableHeight - WINDOW_FRAME_WIDTH;
  }
}
